import React, { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Link as LinkIcon, Pencil, Trash, Wallet, FileArrowUp, FileArrowDown, FileText, Copy, HandCoins } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { PaymentStatusBadge } from "@/components/StatusBadge";
import RequestPaymentDialog from "@/components/RequestPaymentDialog";
import { SortHead, PlatformBadge, MarginPill, useSortedList, fmtInt, fmtCur } from "./utils";
import { LiveCell } from "./LiveCell";
import { RefreshInstagramButton } from "./RefreshInstagramButton";
import { PaymentRequestsCell } from "./PaymentRequestsCell";
import { useInvoiceViewer } from "@/components/InvoiceViewer";

export function LiveTable({
  list, onEdit, onDelete, onPay, onOpenImport, onCreatorUpdated,
  onRefreshCampaignId, campaignType, onFullReload, isAdmin, campaignName,
  paymentRequestsByCreator,
}) {
  const { sorted, sort, toggle } = useSortedList(list, { key: "live_date", dir: "desc" });
  const [requestFor, setRequestFor] = useState(null);
  const { openInvoice } = useInvoiceViewer();
  const lastUpdated = useMemo(() => {
    const stamps = (list || [])
      .map((c) => c.last_ig_sync_at)
      .filter(Boolean)
      .map((s) => new Date(s).getTime())
      .filter((n) => Number.isFinite(n));
    return stamps.length ? new Date(Math.max(...stamps)) : null;
  }, [list]);
  const lastUpdatedLabel = lastUpdated
    ? lastUpdated.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : null;

  const exportCsv = () => {
    const cols = [
      ["Username", "username"], ["City", "city"], ["Language", "language"],
      ["Live Date", "live_date"], ["Live Link", "live_link"],
      ["Views", "views"], ["Likes", "likes"], ["Comments", "comments"], ["Shares", "shares"],
    ];
    const escape = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = cols.map(([label]) => escape(label)).join(",");
    const rows = (list || []).map((c) => cols.map(([, k]) => escape(c[k] ?? "")).join(","));
    const csv = "\uFEFF" + [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeName = String(campaignName || "campaign").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `${safeName}-live-metrics-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${list.length} row${list.length === 1 ? "" : "s"}`, { duration: 5000 });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-slate-500">
          {list.length === 0 ? "No live videos yet — move a creator's script status to <b>Live</b> to see them here." : `${list.length} live creator${list.length > 1 ? "s" : ""} · metrics save on blur`}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {lastUpdatedLabel && (
            <div className="text-[10px] text-slate-500" data-testid="live-last-updated">
              Last Updated: <span className="font-medium text-slate-700">{lastUpdatedLabel}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            {isAdmin && (
              <RefreshInstagramButton campaignId={onRefreshCampaignId} onDone={onFullReload || onCreatorUpdated} />
            )}
            <Button
              variant="outline"
              onClick={onOpenImport}
              className="border-slate-300 h-9 shrink-0"
              data-testid="import-metrics-btn"
            >
              <FileArrowUp size={14} className="mr-1.5" /> Import metrics
            </Button>
            <Button
              variant="outline"
              onClick={exportCsv}
              disabled={list.length === 0}
              className="border-slate-300 h-9 shrink-0"
              data-testid="export-metrics-btn"
            >
              <FileArrowDown size={14} className="mr-1.5" /> Export CSV
            </Button>
          </div>
        </div>
      </div>

      {list.length === 0 ? (
        <Card className="p-12 text-center border border-dashed border-slate-300 shadow-none text-sm text-slate-500">
          No live videos yet. Change a creator's script status to <b>Live</b> to see them here.
        </Card>
      ) : (
        <Card className="border border-slate-200 shadow-none overflow-hidden">
          <div className="overflow-auto max-h-[70vh]">
            <Table>
              <TableHeader className="bg-slate-50 sticky top-0 z-10 shadow-[0_1px_0_0_rgba(0,0,0,0.06)]">
                <TableRow>
                  <SortHead label="Creator" k="username" sort={sort} onToggle={toggle} className="min-w-[140px]" />
                  <SortHead label="City" k="city" sort={sort} onToggle={toggle} />
                  <SortHead label="Language" k="language" sort={sort} onToggle={toggle} />
                  <SortHead label="Final cost" k="final_cost" sort={sort} onToggle={toggle} className="text-right" />
                  {campaignType === "Creator Based" && (
                    <>
                      <SortHead label="Internal" k="internal_cost" sort={sort} onToggle={toggle} className="text-right min-w-[100px]" />
                      <SortHead label="Client" k="client_cost" sort={sort} onToggle={toggle} className="text-right min-w-[100px]" />
                      <TableHead className="text-right min-w-[110px]">Margin</TableHead>
                    </>
                  )}
                  <SortHead label="Paid" k="total_paid" sort={sort} onToggle={toggle} className="text-right" />
                  <TableHead>Payment</TableHead>
                  <TableHead>Invoice</TableHead>
                  <SortHead label="Live" k="live_date" sort={sort} onToggle={toggle} className="min-w-[120px]" />
                  <SortHead label="Views" k="views" sort={sort} onToggle={toggle} className="text-right min-w-[110px]" />
                  <SortHead label="Likes" k="likes" sort={sort} onToggle={toggle} className="text-right min-w-[110px]" />
                  <SortHead label="Comments" k="comments" sort={sort} onToggle={toggle} className="text-right min-w-[110px]" />
                  <SortHead label="Shares" k="shares" sort={sort} onToggle={toggle} className="text-right min-w-[110px]" />
                  <TableHead className="text-center w-[110px]">Comments made?</TableHead>
                  <SortHead label="# Comments" k="comments_made_count" sort={sort} onToggle={toggle} className="text-right min-w-[110px]" />
                  <TableHead className="text-center w-[70px]">Script</TableHead>
                  <TableHead className="text-right w-[140px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((c) => (
                  <TableRow key={c.id} className="hover:bg-slate-50/60" data-testid={`live-row-${c.id}`}>
                    <TableCell>
                      {c.link ? (
                        <a href={c.link} target="_blank" rel="noreferrer" className="font-medium text-slate-900 hover:text-blue-600 hover:underline inline-flex items-center gap-1" data-testid={`open-creator-${c.id}`}>
                          @{c.username}{c.platform ? <PlatformBadge p={c.platform} /> : null}
                          <LinkIcon size={11} className="opacity-50" />
                        </a>
                      ) : (
                        <div className="font-medium text-slate-900" data-testid={`open-creator-${c.id}`}>@{c.username}{c.platform ? <PlatformBadge p={c.platform} /> : null}</div>
                      )}
                      <div className="text-xs text-slate-500">{fmtInt(c.followers)} followers</div>
                    </TableCell>
                    <TableCell className="text-slate-700 text-xs">{c.city || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="text-slate-700 text-xs">{c.language || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="text-right font-mono text-xs font-semibold text-slate-900">{fmtCur(c.final_cost)}</TableCell>
                    {campaignType === "Creator Based" && (
                      <>
                        <TableCell className="text-right font-mono text-xs text-slate-700" data-testid={`live-internal-${c.id}`}>{fmtCur(c.internal_cost)}</TableCell>
                        <TableCell className="text-right font-mono text-xs text-slate-700" data-testid={`live-client-${c.id}`}>{fmtCur(c.client_cost)}</TableCell>
                        <TableCell className="text-right font-mono text-xs font-semibold" data-testid={`live-margin-${c.id}`}>
                          <MarginPill internal={c.internal_cost} client={c.client_cost} />
                        </TableCell>
                      </>
                    )}
                    <TableCell className="text-right font-mono text-xs" data-testid={`live-paid-${c.id}`}>
                      {Number(c.total_paid || 0) > 0 ? (
                        <span className="font-semibold text-emerald-700">{fmtCur(c.total_paid)}</span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <PaymentRequestsCell creator={c} requests={(paymentRequestsByCreator && paymentRequestsByCreator[c.id]) || []} />
                    </TableCell>
                    <TableCell>
                      {c.invoice_file_id ? (
                        <button
                          type="button"
                          onClick={() => openInvoice(c.invoice_file_id, c.invoice_filename)}
                          className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 text-xs"
                          data-testid={`live-invoice-link-${c.id}`}
                        >
                          <FileText size={11} /> {c.invoice_type}
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">{c.invoice_type || "—"}</span>
                      )}
                    </TableCell>
                    <TableCell className="min-w-[120px]" data-testid={`live-col-${c.id}`}>
                      {c.live_link ? (
                        <a
                          href={c.live_link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:text-blue-700 hover:underline inline-flex items-center gap-1 text-xs font-medium"
                          data-testid={`live-view-${c.id}`}
                        >
                          <LinkIcon size={11} /> View
                        </a>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                      {c.live_date && (
                        <div className="text-[10px] text-slate-500 font-mono mt-0.5" data-testid={`live-date-${c.id}`}>
                          {new Date(c.live_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                        </div>
                      )}
                    </TableCell>
                    <TableCell><LiveCell creator={c} field="views" type="number" onUpdated={onCreatorUpdated} /></TableCell>
                    <TableCell><LiveCell creator={c} field="likes" type="number" onUpdated={onCreatorUpdated} /></TableCell>
                    <TableCell><LiveCell creator={c} field="comments" type="number" onUpdated={onCreatorUpdated} /></TableCell>
                    <TableCell><LiveCell creator={c} field="shares" type="number" onUpdated={onCreatorUpdated} /></TableCell>
                    <TableCell className="text-center" data-testid={`live-comments-made-${c.id}`}>
                      <input
                        type="checkbox"
                        checked={!!c.comments_made}
                        onChange={async (e) => {
                          const next = e.target.checked;
                          try {
                            const { data } = await api.put(`/creators/${c.id}`, { comments_made: next });
                            onCreatorUpdated?.(data);
                          } catch { toast.error("Save failed"); }
                        }}
                        className="w-4 h-4 accent-blue-600 cursor-pointer"
                      />
                    </TableCell>
                    <TableCell><LiveCell creator={c} field="comments_made_count" type="number" onUpdated={onCreatorUpdated} /></TableCell>
                    <TableCell className="text-center">
                      <button
                        onClick={async () => {
                          const text = c.script_text || "";
                          if (!text.trim()) { toast.warning("No script saved for this creator"); return; }
                          try {
                            await navigator.clipboard.writeText(text);
                            toast.success(`Script copied · ${text.length} chars`);
                          } catch { toast.error("Copy failed"); }
                        }}
                        className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Copy script to clipboard"
                        data-testid={`copy-script-${c.id}`}
                      >
                        <Copy size={14} />
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => onPay(c)} className="p-1.5 text-emerald-600 hover:text-white hover:bg-emerald-600 rounded transition-colors" title="Log payment" data-testid={`pay-${c.id}`}>
                          <Wallet size={14} />
                        </button>
                        <button onClick={() => setRequestFor(c)} className="p-1.5 text-amber-600 hover:text-white hover:bg-amber-600 rounded transition-colors" title="Request payment" data-testid={`request-pay-${c.id}`}>
                          <HandCoins size={14} />
                        </button>
                        <button onClick={() => onEdit(c)} className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded" data-testid={`edit-${c.id}`}>
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => onDelete(c)} className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded" data-testid={`delete-${c.id}`}>
                          <Trash size={14} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
      <RequestPaymentDialog open={!!requestFor} onOpenChange={(v) => !v && setRequestFor(null)} creator={requestFor} onCreated={() => { setRequestFor(null); onFullReload?.(); }} />
    </div>
  );
}
